import { useCallback, useRef, useState } from 'react';
import { API_URL, apiFetch } from '../config/api';

// Chrome/Firefox give webm/opus; Safari only does mp4. Whisper takes either.
const MEDIA_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

export function speak(text) {
    if (!text || typeof window === 'undefined' || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.lang = 'en-IN';
    window.speechSynthesis.speak(utterance);
}

// getUserMedia is secure-context only — so a plain-http origin that is not
// localhost kills recording as surely as it kills speech recognition, and is
// also why the permission prompt never sticks.
export function micDiagnosis() {
    if (typeof window === 'undefined') return { ok: false, reason: '', facts: '' };
    const canRecord = !!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder);
    const facts = `${window.location.host} · secure ${window.isSecureContext ? 'yes' : 'NO'}`
        + ` · recorder ${canRecord ? 'ok' : 'MISSING'}`;
    if (canRecord) return { ok: true, reason: '', facts };
    return {
        ok: false,
        facts,
        reason: window.isSecureContext
            ? 'Is browser me audio recording nahi hai. Chrome ya Edge use karo.'
            : `Mic sirf https ya localhost pe milta hai — http://localhost:${window.location.port || 3000} kholo.`,
    };
}

/**
 * Record, then transcribe on the server. Replaces the Web Speech API, which is
 * absent outside Chromium, absent in Electron and IDE webviews, and disabled in
 * Brave. MediaRecorder works everywhere getUserMedia does.
 *
 * onText(text)     — the transcript, once the server returns one
 * onStatus(msg)    — a line to show the operator ('' clears it)
 * onError(message) — recording or transcription failed
 */
export function usePushToTalk({ onText, onStatus = () => {}, onError = () => {} }) {
    const [listening, setListening] = useState(false);
    // The gap between "stopped recording" and "server answered" — the mic must
    // stay disabled through it or a second press starts an overlapping take.
    const [transcribing, setTranscribing] = useState(false);
    const recorderRef = useRef(null);
    const mic = micDiagnosis();

    const start = useCallback(async () => {
        if (recorderRef.current || !mic.ok) return;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            });
            const mimeType = MEDIA_TYPES.find((t) => window.MediaRecorder.isTypeSupported?.(t)) || '';
            const recorder = new window.MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            const chunks = [];

            recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
            recorder.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
                // Anything this short is a mis-tap, not an utterance.
                if (blob.size < 2000) { onStatus(''); return; }
                setTranscribing(true);
                onStatus('Transcribing…');
                try {
                    const ext = (recorder.mimeType || '').includes('mp4') ? 'mp4' : 'webm';
                    const res = await apiFetch(`${API_URL}/nl/stt?ext=${ext}`, {
                        method: 'POST',
                        headers: { 'Content-Type': blob.type || 'audio/webm' },
                        body: blob,
                    });
                    const data = await res.json();
                    if (!data.success) { onStatus(''); onError(data.message); return; }
                    if (!data.text) { onStatus('Kuch sunai nahi diya.'); return; }
                    onStatus('');
                    await onText(data.text);
                } catch (err) {
                    onStatus('');
                    onError(err.message);
                } finally {
                    setTranscribing(false);
                }
            };

            recorder.start();
            recorderRef.current = recorder;
            setListening(true);
            onStatus('');
        } catch (err) {
            onStatus('');
            onError(err.name === 'NotAllowedError' ? 'Mic permission block hai.' : err.message);
        }
    }, [mic.ok, onText, onStatus, onError]);

    const stop = useCallback(() => {
        setListening(false);
        const recorder = recorderRef.current;
        recorderRef.current = null;
        try { if (recorder?.state === 'recording') recorder.stop(); } catch { /* already stopped */ }
    }, []);

    return { listening, transcribing, start, stop, mic };
}

export default usePushToTalk;
